import type { TourAction, TourDefinition, TourPosition, TourStep } from './types'
import { anchorSelector, isKnownAnchor } from './tourAnchorManifest'
import { isWorkspaceAction } from '../../utils/workspacePermissions'

const tourModules = import.meta.glob('/src/assets/tours/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, unknown>

const positions = new Set<TourPosition>(['top', 'right', 'bottom', 'left', 'auto'])
const actions = new Set<TourAction>(['click', 'input', 'wait'])

const validationError = (journeyId: string, message: string) => {
  if (import.meta.env.DEV) console.error(`[TOURGUIDE] Invalid journey "${journeyId}": ${message}`)
  return null
}

/**
 * Extract the tour anchor id from a targetSelector of the form
 * `[data-tour-id="..."]`. Returns null for unstructured selectors so the
 * built-in dashboard-init tour (legacy hand-authored selector) still loads.
 */
const extractAnchorId = (selector: string): string | null => {
  const match = selector.match(/^\[data-tour-id="([^"]+)"\]$/)
  return match ? match[1] : null
}

export type TourValidationMode = 'lenient' | 'strict'

export const validateTour = (value: unknown, source = 'unknown', mode: TourValidationMode = 'lenient'): TourDefinition | null => {
  if (!value || typeof value !== 'object') return validationError(source, 'root must be an object')
  const candidate = value as Partial<TourDefinition>
  const journeyId = typeof candidate.id === 'string' && candidate.id ? candidate.id : source
  if (!candidate.id || typeof candidate.id !== 'string') return validationError(journeyId, 'missing id')
  if (!Array.isArray(candidate.steps) || candidate.steps.length === 0) return validationError(journeyId, 'steps must be a non-empty array')
  if (candidate.seenScope !== undefined && candidate.seenScope !== 'user' && candidate.seenScope !== 'workspace') {
    return validationError(journeyId, 'seenScope must be "user" or "workspace"')
  }
  if (candidate.requiredAction !== undefined && !isWorkspaceAction(candidate.requiredAction)) {
    return validationError(journeyId, `invalid requiredAction "${String(candidate.requiredAction)}"`)
  }

  for (const [index, rawStep] of candidate.steps.entries()) {
    if (!rawStep || typeof rawStep !== 'object') return validationError(journeyId, `step ${index + 1} must be an object`)
    const step = rawStep as Partial<TourStep>
    for (const key of ['id', 'title', 'message', 'targetSelector', 'screenPath'] as const) {
      if (typeof step[key] !== 'string' || !step[key]) return validationError(journeyId, `step ${index + 1} missing ${key}`)
    }
    if (step.position && !positions.has(step.position)) return validationError(journeyId, `step ${index + 1} has invalid position`)
    if (step.action && !actions.has(step.action)) return validationError(journeyId, `step ${index + 1} has invalid action`)
    if (step.targetTimeoutMs !== undefined && (typeof step.targetTimeoutMs !== 'number' || step.targetTimeoutMs < 1000 || step.targetTimeoutMs > 120000)) {
      return validationError(journeyId, `step ${index + 1} targetTimeoutMs must be between 1000 and 120000`)
    }
    if (step.action === 'wait' && (typeof step.durationMs !== 'number' || step.durationMs < 0)) {
      return validationError(journeyId, `step ${index + 1} wait action requires durationMs`)
    }
    if (step.action === 'input' && (typeof step.selector !== 'string' || typeof step.criteria !== 'string')) {
      return validationError(journeyId, `step ${index + 1} input action requires selector and criteria`)
    }
    // Strict mode (used for AI-generated tours): every selector must point at
    // a known anchor in the manifest. The lenient path stays permissive so
    // hand-authored bundled tours can still reference one-off DOM selectors.
    if (mode === 'strict') {
      const targetAnchor = extractAnchorId(step.targetSelector!)
      if (!targetAnchor || !isKnownAnchor(targetAnchor)) {
        return validationError(journeyId, `step ${index + 1} targetSelector "${step.targetSelector}" is not in the anchor manifest`)
      }
      if (step.action === 'input' && step.selector) {
        const inputAnchor = extractAnchorId(step.selector)
        if (!inputAnchor || !isKnownAnchor(inputAnchor)) {
          return validationError(journeyId, `step ${index + 1} input selector "${step.selector}" is not in the anchor manifest`)
        }
      }
    }
  }
  return candidate as TourDefinition
}

const registry = new Map<string, TourDefinition>()
const ephemeralIds = new Set<string>()

for (const [path, value] of Object.entries(tourModules)) {
  const tour = validateTour(value, path.split('/').pop() ?? path)
  if (tour) registry.set(tour.id, tour)
}

export const getTour = (id: string): TourDefinition | null => registry.get(id) ?? null
export const getRegisteredTourIds = (): string[] => [...registry.keys()]

/**
 * Register a tour at runtime — used by the AI tour generator so the freshly
 * built JSON can be played through the existing TourGuideProvider. Returns
 * the tour id on success, throws on validation failure. The id is namespaced
 * with `ai-` to keep the corpus distinct from bundled tours.
 *
 * Validation is strict: every selector must resolve to a known anchor.
 */
export const registerEphemeralTour = (tour: TourDefinition): string => {
  const validated = validateTour(tour, tour.id, 'strict')
  if (!validated) throw new Error(`[TOURGUIDE] Ephemeral tour "${tour.id}" failed strict validation`)
  registry.set(validated.id, validated)
  ephemeralIds.add(validated.id)
  return validated.id
}

export const unregisterEphemeralTour = (id: string): void => {
  if (!ephemeralIds.has(id)) return
  registry.delete(id)
  ephemeralIds.delete(id)
}

export const isEphemeralTour = (id: string): boolean => ephemeralIds.has(id)

/** Re-export so the AI generator can build selectors without importing the manifest directly. */
export { anchorSelector }
